import React, { useContext, useState, useRef, useCallback, useMemo } from "react";
import ForecastDashboard, { ForecastDashboardContext } from "./ForecastDashboard";
import EconModal from "./EconModal";
import { DEMO_LEASES, computeEconWindow } from "./econEngine";
import "./ForecastCockpit.css";

/* ═══════════════════════════════════════════════════════════════════════
 * ForecastCockpit.js — Operational Intelligence Cockpit
 * Data-driven: selectedLeaseIdx drives chart + panels + econ
 * ═══════════════════════════════════════════════════════════════════════ */

const fin = (n) => Number.isFinite(n);
const money = (n, d = 0) => {
  if (!fin(n)) return "\u2014";
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: d }).format(n);
};
const fmtN = (n, d = 1) => fin(n) ? new Intl.NumberFormat(undefined, { maximumFractionDigits: d }).format(n) : "\u2014";

/* ─── Hover interpolation ─── */
function interpY(pts, svgX) {
  if (!pts || !pts.length) return null;
  if (svgX <= pts[0][0]) return pts[0][1];
  if (svgX >= pts[pts.length - 1][0]) return pts[pts.length - 1][1];
  for (let i = 0; i < pts.length - 1; i++) {
    if (svgX >= pts[i][0] && svgX <= pts[i + 1][0]) {
      const t = (svgX - pts[i][0]) / (pts[i + 1][0] - pts[i][0]);
      return pts[i][1] + t * (pts[i + 1][1] - pts[i][1]);
    }
  }
  return null;
}
function interpVal(valPts, svgX) {
  if (!valPts || !valPts.length) return null;
  if (svgX <= valPts[0][0]) return valPts[0][1];
  if (svgX >= valPts[valPts.length - 1][0]) return valPts[valPts.length - 1][1];
  for (let i = 0; i < valPts.length - 1; i++) {
    if (svgX >= valPts[i][0] && svgX <= valPts[i + 1][0]) {
      const t = (svgX - valPts[i][0]) / (valPts[i + 1][0] - valPts[i][0]);
      return valPts[i][1] + t * (valPts[i + 1][1] - valPts[i][1]);
    }
  }
  return null;
}
function svgXToDate(svgX) {
  const t = (svgX - 60) / 700;
  const d0 = new Date(2024, 8, 1);
  const d = new Date(d0.getTime() + t * 120 * 86400000);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/* ═══ HERO CHART — data-driven SVG ═══ */
function HeroChart({ lease, showOil, showGas, showWater, showBands, showMA }) {
  const containerRef = useRef(null);
  const [hover, setHover] = useState(null);

  const handleMove = useCallback((e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const rx = (e.clientX - rect.left) / rect.width;
    const ry = (e.clientY - rect.top) / rect.height;
    const svgX = 60 + rx * 700;
    if (svgX < 60 || svgX > 760) { setHover(null); return; }
    setHover({ px: e.clientX - rect.left, py: e.clientY - rect.top, svgX, rx, ry });
  }, []);
  const handleLeave = useCallback(() => setHover(null), []);

  const P = lease.chart.paths;
  const curveDefs = lease.chart.curveDefs;
  const todayX = lease.chart.todayX;
  const isHover = hover != null;
  const hoverSvgX = hover?.svgX;
  const hDim = isHover ? 0.5 : 1;

  // Hover data
  const hoverVals = useMemo(() => {
    if (!isHover) return [];
    const vis = { Oil: showOil, Gas: showGas, Water: showWater };
    return curveDefs.filter(c => vis[c.id]).map(c => ({
      id: c.id, color: c.bright, unit: c.unit,
      y: interpY(c.pts, hoverSvgX),
      val: interpVal(c.valPts, hoverSvgX),
    }));
  }, [isHover, hoverSvgX, curveDefs, showOil, showGas, showWater]);
  const hoverDate = isHover ? svgXToDate(hoverSvgX) : "";

  // Forecast region X range
  const fcstStartX = todayX;

  return (
    <div className="ck-hero" ref={containerRef} onMouseMove={handleMove} onMouseLeave={handleLeave}>
      <svg className="ck-hero-svg" viewBox="0 0 800 400" preserveAspectRatio="none">
        <defs>
          <linearGradient id="bG" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3ee8a0" stopOpacity="0.30" />
            <stop offset="40%" stopColor="#1a9060" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#1a9060" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="bR" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f08888" stopOpacity="0.24" />
            <stop offset="40%" stopColor="#a04848" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#a04848" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="bB" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#70b0e8" stopOpacity="0.26" />
            <stop offset="40%" stopColor="#3870a0" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#3870a0" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="tH" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#60c8d8" stopOpacity="0" />
            <stop offset="35%" stopColor="#60c8d8" stopOpacity="0.04" />
            <stop offset="50%" stopColor="#78e0f0" stopOpacity="0.10" />
            <stop offset="65%" stopColor="#60c8d8" stopOpacity="0.04" />
            <stop offset="100%" stopColor="#60c8d8" stopOpacity="0" />
          </linearGradient>
          <radialGradient id="atm1" cx="48%" cy="28%" r="42%">
            <stop offset="0%" stopColor="#182c58" stopOpacity="0.24" />
            <stop offset="100%" stopColor="#0c1830" stopOpacity="0" />
          </radialGradient>
          <filter id="f4"><feGaussianBlur stdDeviation="4" /></filter>
          <filter id="f8"><feGaussianBlur stdDeviation="8" /></filter>
          <filter id="f14"><feGaussianBlur stdDeviation="14" /></filter>
          <filter id="f2g"><feGaussianBlur stdDeviation="2" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
          <filter id="f3g"><feGaussianBlur stdDeviation="3" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
          <filter id="fPt"><feGaussianBlur stdDeviation="3.5" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
          <linearGradient id="hCur" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#90d0e8" stopOpacity="0" />
            <stop offset="40%" stopColor="#90d0e8" stopOpacity="0.07" />
            <stop offset="50%" stopColor="#a0e0f0" stopOpacity="0.12" />
            <stop offset="60%" stopColor="#90d0e8" stopOpacity="0.07" />
            <stop offset="100%" stopColor="#90d0e8" stopOpacity="0" />
          </linearGradient>
          {/* Forecast region fill */}
          <linearGradient id="fcstFill" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#3060a0" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#2050a0" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Atmosphere */}
        <rect width="800" height="400" fill="url(#atm1)" />

        {/* Grid */}
        {[80,160,240,320].map(y => <line key={y} x1="40" y1={y} x2="760" y2={y} stroke="rgba(20,38,72,0.14)" strokeWidth="0.5" strokeDasharray="3,10" />)}
        {[120,240,360,480,600,720].map(x => <line key={x} x1={x} y1="20" x2={x} y2="380" stroke="rgba(20,38,72,0.09)" strokeWidth="0.4" strokeDasharray="3,10" />)}

        {/* Forecast region shading */}
        <rect x={fcstStartX} y="0" width={760 - fcstStartX} height="400" fill="url(#fcstFill)" />
        <text x={fcstStartX + 8} y="18" fill="#4a6888" fontSize="8" fontFamily="'JetBrains Mono',monospace" opacity="0.6">FORECAST</text>

        {/* TODAY beam */}
        <rect x={fcstStartX - 80} y="0" width="160" height="400" fill="url(#tH)" opacity={hDim} />
        <line x1={fcstStartX} y1="0" x2={fcstStartX} y2="400" stroke="#88e8f8" strokeWidth="4" opacity={isHover?0.05:0.14} filter="url(#f4)" />
        <line x1={fcstStartX} y1="0" x2={fcstStartX} y2="400" stroke="#b0f4ff" strokeWidth="1.2" opacity={isHover?0.4:0.75} />

        {/* Confidence bands */}
        {showBands && showOil && P.oilBandU && (
          <g style={{mixBlendMode:'screen'}}>
            <path d={`${P.oilBandU} ${P.oilBandL} Z`} fill="url(#bG)" opacity="0.7" />
          </g>
        )}
        {showBands && showGas && P.gasBandU && (
          <g style={{mixBlendMode:'screen'}}>
            <path d={`${P.gasBandU} ${P.gasBandL} Z`} fill="url(#bR)" opacity="0.6" />
          </g>
        )}
        {showBands && showWater && P.watBandU && (
          <g style={{mixBlendMode:'screen'}}>
            <path d={`${P.watBandU} ${P.watBandL} Z`} fill="url(#bB)" opacity="0.5" />
          </g>
        )}

        {/* 7D Moving Average */}
        {showMA && showOil && P.oilMA && (
          <path d={P.oilMA} fill="none" stroke="#2a8a62" strokeWidth="1.5" opacity="0.4" strokeDasharray="4,3" />
        )}
        {showMA && showGas && P.gasMA && (
          <path d={P.gasMA} fill="none" stroke="#a85050" strokeWidth="1.5" opacity="0.4" strokeDasharray="4,3" />
        )}
        {showMA && showWater && P.watMA && (
          <path d={P.watMA} fill="none" stroke="#4070a8" strokeWidth="1.5" opacity="0.4" strokeDasharray="4,3" />
        )}

        {/* ── CURVES — 3-layer: haze / glow / filament ── */}
        {showOil && <>
          <path d={P.oilH} fill="none" stroke="#2a8a62" strokeWidth="12" opacity="0.10" filter="url(#f8)" />
          <path d={P.oilH} fill="none" stroke="#3cc888" strokeWidth="4" opacity="0.25" filter="url(#f3g)" />
          <path d={P.oilH} fill="none" stroke="#5afcb8" strokeWidth="2" opacity={isHover?0.7:0.92} filter="url(#f2g)" />
          <path d={P.oilF} fill="none" stroke="#3cc888" strokeWidth="6" opacity="0.06" filter="url(#f8)" />
          <path d={P.oilF} fill="none" stroke="#5afcb8" strokeWidth="1.5" strokeDasharray="6,4" opacity="0.65" />
        </>}
        {showGas && <>
          <path d={P.gasH} fill="none" stroke="#a85050" strokeWidth="10" opacity="0.08" filter="url(#f8)" />
          <path d={P.gasH} fill="none" stroke="#d07070" strokeWidth="3.5" opacity="0.22" filter="url(#f3g)" />
          <path d={P.gasH} fill="none" stroke="#f09898" strokeWidth="1.8" opacity={isHover?0.6:0.88} filter="url(#f2g)" />
          <path d={P.gasF} fill="none" stroke="#d07070" strokeWidth="5" opacity="0.05" filter="url(#f8)" />
          <path d={P.gasF} fill="none" stroke="#f09898" strokeWidth="1.3" strokeDasharray="6,4" opacity="0.55" />
        </>}
        {showWater && <>
          <path d={P.watH} fill="none" stroke="#4070a8" strokeWidth="10" opacity="0.08" filter="url(#f8)" />
          <path d={P.watH} fill="none" stroke="#5a90d0" strokeWidth="3.5" opacity="0.20" filter="url(#f3g)" />
          <path d={P.watH} fill="none" stroke="#80c0f0" strokeWidth="1.8" opacity={isHover?0.5:0.80} filter="url(#f2g)" />
          <path d={P.watF} fill="none" stroke="#5a90d0" strokeWidth="5" opacity="0.05" filter="url(#f8)" />
          <path d={P.watF} fill="none" stroke="#80c0f0" strokeWidth="1.3" strokeDasharray="6,4" opacity="0.50" />
        </>}

        {/* TODAY label */}
        <text x={fcstStartX} y="14" textAnchor="middle" fill="#b0f8ff" fontSize="11" fontWeight="900" fontFamily="'JetBrains Mono',monospace" letterSpacing="0.08em" opacity={isHover?0.3:0.8} filter="url(#f2g)">TODAY</text>

        {/* ── Derived annotations ── */}
        {!isHover && (lease.annotations || []).map((ann, ai) => {
          const ax = 60 + ann.x * 700;
          const streamDef = curveDefs.find(c => c.id === ann.stream);
          const ay = streamDef ? interpY(streamDef.pts, ax) : 200;
          if (ay == null) return null;
          const labelY = ay - 18;
          return (
            <g key={ai} opacity="0.85">
              <circle cx={ax} cy={ay} r="3" fill={ann.color} opacity="0.7" filter="url(#f2g)" />
              <circle cx={ax} cy={ay} r="1.2" fill="#fff" opacity="0.6" />
              <text x={ax} y={Math.max(20, labelY)} textAnchor="middle" fill={ann.color} fontSize="8" fontWeight="700" fontFamily="'JetBrains Mono',monospace" opacity="0.8">{ann.label}</text>
            </g>
          );
        })}

        {/* Axis labels */}
        {[["60","Sep"],["200","Oct"],["600","Nov"],["730","Dec"]].map(([x,l]) =>
          <text key={l} x={x} y="395" fill="#3a4c65" fontSize="8" fontFamily="'JetBrains Mono',monospace">{l}</text>
        )}

        {/* ── HOVER ── */}
        {isHover && (
          <g>
            <rect x={hoverSvgX-35} y="0" width="70" height="400" fill="url(#hCur)" />
            <line x1={hoverSvgX} y1="20" x2={hoverSvgX} y2="380" stroke="#90d0e0" strokeWidth="0.8" opacity="0.5" strokeDasharray="2,4" />
            {hoverVals.map(v => v.y != null && (
              <g key={v.id}>
                <circle cx={hoverSvgX} cy={v.y} r="6" fill={v.color} opacity="0.12" filter="url(#fPt)" />
                <circle cx={hoverSvgX} cy={v.y} r="3" fill={v.color} opacity="0.9" />
                <circle cx={hoverSvgX} cy={v.y} r="1.2" fill="#fff" opacity="0.7" />
              </g>
            ))}
          </g>
        )}
      </svg>

      {/* Tooltip */}
      {isHover && hoverVals.length > 0 && (
        <div className="ck-tooltip" style={{
          left: hover.px + (hover.rx > 0.7 ? -155 : 16),
          top: Math.max(10, hover.py - 60),
        }}>
          <div className="ck-tt-date">{hoverDate}</div>
          {hoverVals.map(v => (
            <div key={v.id} className="ck-tt-row">
              <span className="ck-tt-dot" style={{ background: v.color, boxShadow: `0 0 4px ${v.color}` }} />
              <span className="ck-tt-name">{v.id}</span>
              <span className="ck-tt-val">{fin(v.val) ? fmtN(v.val) : "\u2014"} {v.unit}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Mini sparkline ─── */
function Spark({ positive, seed = 0 }) {
  const pts = [];
  let y = 50;
  for (let i = 0; i < 16; i++) {
    const trend = positive ? -0.8 : 0.6;
    y = Math.max(10, Math.min(90, y + trend + Math.sin((i + seed) * 1.4) * 10));
    pts.push(`${(i / 15) * 60},${y}`);
  }
  const col = positive ? "#3cc888" : "#d07070";
  const bright = positive ? "#5afcb8" : "#f09898";
  return (
    <svg width="64" height="28" viewBox="0 0 60 100" preserveAspectRatio="none" className="ck-spark">
      <defs>
        <linearGradient id={`sp${seed}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={col} stopOpacity="0.35" />
          <stop offset="60%" stopColor={col} stopOpacity="0.08" />
          <stop offset="100%" stopColor={col} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`M${pts.join(" L")} L60,100 L0,100 Z`} fill={`url(#sp${seed})`} />
      <path d={`M${pts.join(" L")}`} fill="none" stroke={bright} strokeWidth="1.5" vectorEffect="non-scaling-stroke" opacity="0.85" />
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
 * COCKPIT INNER
 * ═══════════════════════════════════════════════════════════════════════ */
function CockpitInner() {
  // Central lease selection — drives everything
  const [selectedLeaseIdx, setSelectedLeaseIdx] = useState(0);
  const [ckEconOpen, setCkEconOpen] = useState(false);

  // Chart controls
  const [showOil, setShowOil] = useState(true);
  const [showGas, setShowGas] = useState(true);
  const [showWater, setShowWater] = useState(true);
  const [showBands, setShowBands] = useState(true);
  const [showMA, setShowMA] = useState(false);

  const ctx = useContext(ForecastDashboardContext);
  const init = ctx?.init;
  const latencyMs = ctx?.latencyMs;
  const runAt = ctx?.runAt;
  const data = ctx?.data;
  const error = ctx?.error;
  const hasData = Boolean(data);

  const lease = DEMO_LEASES[selectedLeaseIdx];
  const BASE_SC = useMemo(() => ({ key: "Base", oilMult: 1, gasMult: 1, opexMult: 1 }), []);

  // Compute econ for selected lease using its own defaults
  const econResult = useMemo(() => {
    return computeEconWindow(lease, lease.econDefaults, BASE_SC);
  }, [lease, BASE_SC]);

  // Pre-compute econ summaries for all leases (for left panel)
  const leaseEconSummaries = useMemo(() => {
    return DEMO_LEASES.map(w => {
      const r = computeEconWindow(w, w.econDefaults, BASE_SC);
      return { cumCF: r.cumCF, decision: r.decision, windowDays: w.econDefaults.windowDays || 60 };
    });
  }, [BASE_SC]);

  // Derive action/recommendation from econ
  const decision = econResult.decision;
  const actionLabel = decision === "Shut-in candidate" ? "EVALUATE SHUT-IN"
    : decision === "Monitor" ? "MONITOR CLOSELY"
    : "OPTIMIZE PRODUCTION";
  const actionImpact = money(econResult.cumCF, 0);
  const isNegCF = econResult.cumCF < 0;
  const actionConf = econResult.confidence;
  const actionWhy = econResult.decisionDetail;

  // Cards from chart data
  const cards = useMemo(() => {
    const c = lease.chart;
    const oilDelta = c.avg7Oil > 0 ? (c.latestOil - c.avg7Oil) / c.avg7Oil : 0;
    const gasDelta = c.avg7Gas > 0 ? (c.latestGas - c.avg7Gas) / c.avg7Gas : 0;
    return [
      { stream: "Oil", last: c.latestOil, avg7: c.avg7Oil, delta: oilDelta, next7: c.fcst7Oil, unit: "bbl/d" },
      { stream: "Gas", last: c.latestGas, avg7: c.avg7Gas, delta: gasDelta, next7: c.fcst7Gas, unit: "mcf/d" },
      { stream: "Water", last: c.latestWater, avg7: c.avg7Water, delta: null, next7: null, unit: "bbl/d" },
    ];
  }, [lease]);

  return (
    <div className="ck">

      {/* ═══ TOP TELEMETRY STRIP ═══ */}
      <header className="ck-top">
        <div className="ck-top-l">
          <span className="ck-logo" />
          <span className="ck-title">OPERATIONAL INTELLIGENCE COCKPIT</span>
        </div>
        <div className="ck-top-c">
          <span className="ck-tel"><span className="ck-dot-live" />System Health: {hasData && !error ? "99.8%" : "99.8%"}</span>
          <span className="ck-tel">Latency: {fin(latencyMs) ? `${latencyMs}ms` : "45ms"}</span>
          <span className="ck-tel">Last Sync: {runAt || "10:34 AM UTC"}</span>
        </div>
        <div className="ck-top-r">
          <div className="ck-avatar">JT</div>
          <span className="ck-user-name">{init?.companyLabel || "J. Thompson"}</span>
        </div>
      </header>

      {/* Breadcrumb */}
      <nav className="ck-bc">
        <span className="ck-bc-i">Home</span>
        <span className="ck-bc-s">{"\u203A"}</span>
        <span className="ck-bc-i">Wells</span>
        <span className="ck-bc-s">{"\u203A"}</span>
        <span className="ck-bc-i ck-bc-a">{lease.name}</span>
      </nav>

      {/* ═══ MAIN BODY ═══ */}
      <div className="ck-body">

        {/* Icon Rail */}
        <nav className="ck-rail">
          {["\u229E","\uD83D\uDCCA","\u25CE","$","\uD83D\uDCC4","\u2699"].map((ic, i) => (
            <div key={i} className={`ck-rail-btn ${i === 0 ? "ck-rail-active" : ""}`}>
              <div className="ck-rail-icon">{ic}</div>
            </div>
          ))}
        </nav>

        {/* ═══ LEFT: Lease Ranking ═══ */}
        <aside className="ck-left">
          <div className="ck-sec-title ck-left-title">LEASE RANKING</div>
          <div className="ck-left-scroll">
          {DEMO_LEASES.map((w, i) => {
            const isSelected = i === selectedLeaseIdx;
            const es = leaseEconSummaries[i];
            const isNeg = es.cumCF < 0;
            const deltaStr = (isNeg ? "-" : "+") + "$" + fmtN(Math.abs(es.cumCF / Math.max(1, es.windowDays)), 0) + "/day";

            return (
              <div
                key={w.id}
                className={`ck-rank-card ${isSelected ? "ck-rank-top" : i >= 3 ? "ck-rank-dim" : ""} ${isNeg ? "ck-rank-neg" : ""}`}
                onClick={() => setSelectedLeaseIdx(i)}
                style={{ cursor: "pointer" }}
              >
                <div className="ck-rank-head">
                  <div className="ck-rank-meta">
                    <div className="ck-rank-well">{w.name}</div>
                    <div className="ck-rank-basin">{w.basin}</div>
                    <div className="ck-rank-sublabel">{es.decision}</div>
                  </div>
                  <div className="ck-score-wrap">
                    <div className={`ck-score ${w.score >= 70 ? "ck-score-crit" : w.score >= 40 ? "ck-score-warm" : "ck-score-hot"}`}>
                      {w.score}
                    </div>
                    <div className="ck-score-tag">{w.score >= 70 ? "High" : w.score >= 40 ? "Marginal" : "At risk"}</div>
                  </div>
                </div>
                <div className="ck-rank-delta">
                  <div>
                    <div className="ck-rank-dlabel">Avg Daily CF</div>
                    <div className={`ck-rank-dval ${isNeg ? "ck-neg" : "ck-pos"}`}>{deltaStr}</div>
                  </div>
                  <Spark positive={!isNeg} seed={i * 5 + 2} />
                </div>
                <div className="ck-rank-tags">
                  {w.tags.map((t, j) => (
                    <span key={j} className={`ck-tag ${
                      t.includes("Stable") || t.includes("Cash") ? "ck-tag-dim"
                      : t.includes("Uneconomic") || t.includes("Sub-") || t.includes("Burden") ? "ck-tag-alert"
                      : "ck-tag-warn"
                    }`}>{t}</span>
                  ))}
                </div>
              </div>
            );
          })}
          </div>
        </aside>

        {/* ═══ CENTER: Production Forecast ═══ */}
        <main className="ck-center">
          <div className="ck-chart-header">
            <div className="ck-chart-header-l">
              <span className="ck-chart-title">PRODUCTION FORECAST</span>
              <select
                className="ck-lease-sel"
                value={selectedLeaseIdx}
                onChange={e => setSelectedLeaseIdx(Number(e.target.value))}
              >
                {DEMO_LEASES.map((l, i) => (
                  <option key={l.id} value={i}>{l.name} \u2014 {l.basin}</option>
                ))}
              </select>
            </div>
            <div className="ck-chart-pills">
              <span className={`ck-pill ${showOil ? "ck-pill-active" : ""}`} onClick={() => setShowOil(v => !v)} style={{ cursor: "pointer", color: showOil ? "#3cc888" : undefined }}>Oil</span>
              <span className={`ck-pill ${showGas ? "ck-pill-active" : ""}`} onClick={() => setShowGas(v => !v)} style={{ cursor: "pointer", color: showGas ? "#f09898" : undefined }}>Gas</span>
              <span className={`ck-pill ${showWater ? "ck-pill-active" : ""}`} onClick={() => setShowWater(v => !v)} style={{ cursor: "pointer", color: showWater ? "#80c0f0" : undefined }}>Water</span>
              <span className="ck-pill-sep" />
              <span className={`ck-pill ${showBands ? "ck-pill-active" : ""}`} onClick={() => setShowBands(v => !v)} style={{ cursor: "pointer" }}>Bands</span>
              <span className={`ck-pill ${showMA ? "ck-pill-active" : ""}`} onClick={() => setShowMA(v => !v)} style={{ cursor: "pointer" }}>7D Avg</span>
            </div>
          </div>

          {/* KPI strip */}
          <div className="ck-kpi-strip">
            {cards.map(c => (
              <div key={c.stream} className="ck-kpi-chip">
                <span className="ck-kpi-label">{c.stream}</span>
                <span className="ck-kpi-val">{fmtN(c.last, 0)} {c.unit}</span>
                {fin(c.delta) && (
                  <span className={c.delta >= 0 ? "ck-pos" : "ck-neg"} style={{ fontSize: "10px" }}>
                    {c.delta >= 0 ? "\u2191" : "\u2193"}{Math.abs(c.delta * 100).toFixed(0)}%
                  </span>
                )}
              </div>
            ))}
            <div className="ck-kpi-chip">
              <span className="ck-kpi-label">PV</span>
              <span className={`ck-kpi-val ${econResult.cumPV >= 0 ? "ck-pos" : "ck-neg"}`}>{money(econResult.cumPV)}</span>
            </div>
          </div>

          <HeroChart
            lease={lease}
            showOil={showOil}
            showGas={showGas}
            showWater={showWater}
            showBands={showBands}
            showMA={showMA}
          />

          <div className="ck-fd-hidden">
            <ForecastDashboard />
          </div>
        </main>

        {/* ═══ RIGHT: Decision System ═══ */}
        <aside className="ck-right">
          <div className="ck-sec-title">DECISION SYSTEM</div>

          {/* ── Decision + Action block ── */}
          <div className="ck-decision-block">
            <div className="ck-dec-row">
              <div className="ck-dec-main">
                <div className={`ck-dec-action ${isNegCF ? "ck-dec-neg" : "ck-dec-pos"}`}>{actionLabel}</div>
                <div className={`ck-dec-cf ${isNegCF ? "ck-neg" : "ck-pos"}`}>{actionImpact}<span className="ck-dec-cf-period">/60d</span></div>
              </div>
              <div className={`ck-conf-badge ${
                actionConf === "High" ? "ck-conf-hi"
                : actionConf === "Very Low" ? "ck-conf-lo"
                : "ck-conf-mod"
              }`}>{actionConf.toUpperCase()}</div>
            </div>
            <div className="ck-dec-diagnosis">{lease.inference.diagnosis}</div>
            <div className="ck-dec-directive">{lease.inference.directive}</div>
            {lease.inference.signal && (
              <div className="ck-dec-signal">Signal: {lease.inference.signal}</div>
            )}
            <div className="ck-cta-row">
              <button className="ck-cta-1" onClick={() => setCkEconOpen(true)}>SIMULATE</button>
              <button className="ck-cta-2" onClick={() => setCkEconOpen(true)}>DETAILS</button>
            </div>
          </div>

          {/* ── Diagnostic detail — scrollable supporting context ── */}
          <div className="ck-right-scroll">
            <div className="ck-diag-block">
              <div className="ck-diag-line ck-diag-signal"><span className="ck-diag-marker">Signal</span>{lease.inference.whatMatters}</div>
              <div className="ck-diag-line ck-diag-move"><span className="ck-diag-marker">Move</span>{lease.inference.action}</div>
              {lease.inference.econRead && (
                <div className="ck-diag-line ck-diag-econ"><span className="ck-diag-marker">Econ</span>{lease.inference.econRead}</div>
              )}
            </div>

            <div className="ck-diag-quote">{lease.inference.oneLiner}</div>

            <div className="ck-support-row">
              {Object.entries(lease.inference.confidence).map(([k, v]) => (
                <span key={k} className={`ck-conf-chip ck-conf-${v.toLowerCase()}`}>{k}: {v}</span>
              ))}
            </div>

            {lease.inference.evidence && lease.inference.evidence.length > 0 && (
              <div className="ck-evidence-chips">
                {lease.inference.evidence.map((e, i) => (
                  <span key={i} className="ck-ev-chip">{e}</span>
                ))}
              </div>
            )}
          </div>
        </aside>
      </div>

      <EconModal
        open={ckEconOpen}
        onClose={() => setCkEconOpen(false)}
        initialLeaseIdx={selectedLeaseIdx}
        onLeaseChange={setSelectedLeaseIdx}
      />
    </div>
  );
}

export default function ForecastCockpit() {
  return <CockpitInner />;
}
